import { Box } from "@chakra-ui/react";
import { ReactNode } from "react";

/**
 * Chip — pill-shaped tag with optional leading dot or icon.
 * design ref: styles.css:366
 */

export type ChipTone =
  | "default"
  | "azurite"
  | "success"
  | "warning"
  | "error"
  | "solid";

export interface ChipProps {
  tone?: ChipTone;
  /** Colored leading dot. `true` = neutral; pass a tone for a colored dot. */
  dot?: ChipTone | boolean;
  /** Optional leading icon node. */
  icon?: ReactNode;
  children: ReactNode;
}

/* design ref: styles.css:366 — base chip */
const BASE = {
  display: "inline-flex" as const,
  alignItems: "center" as const,
  gap: "1xs", // 6px
  px: "2.5", // 10px
  py: "2xs", // 4px
  fontSize: "xs", // 12px
  fontWeight: 500,
  lineHeight: "1",
  borderRadius: "full", // 100px
  whiteSpace: "nowrap" as const,
  borderWidth: "1px",
  borderStyle: "solid" as const,
};

/* design ref: styles.css:366–384 — tone variants (bg / borderColor / color) */
const TONE_STYLES: Record<
  ChipTone,
  { bg: string; borderColor: string; color: string }
> = {
  // base white-ish chip with neutral border (styles.css:366)
  default: {
    bg: "neutral.25",
    borderColor: "neutral.200",
    color: "neutral.800",
  },
  // .chip--solid-neutral (styles.css:380)
  solid: {
    bg: "neutral.100",
    borderColor: "transparent",
    color: "neutral.800",
  },
  // .chip--azurite (styles.css:381)
  azurite: {
    bg: "p.100",
    borderColor: "transparent",
    color: "p.700",
  },
  // .chip--success (styles.css:382)
  success: {
    bg: "success.50",
    borderColor: "success.200",
    color: "success.800",
  },
  // .chip--warning (styles.css:383)
  warning: {
    bg: "warning.50",
    borderColor: "warning.200",
    color: "warning.800",
  },
  // .chip--error (styles.css:384)
  error: {
    bg: "error.50",
    borderColor: "error.200",
    color: "error.800",
  },
};

/* design ref: styles.css:392–398 — .chip__dot (12×8, radius 100px) */
const DOT_BG: Record<ChipTone, string> = {
  default: "neutral.500",
  solid: "neutral.500",
  azurite: "p.500",
  success: "success.500",
  warning: "warning.500",
  error: "error.500",
};

const ChipDot = ({ tone }: { tone: ChipTone }) => (
  <Box
    w="12px"
    h="8px"
    borderRadius="full"
    bg={DOT_BG[tone]}
    flexShrink={0}
    aria-hidden
  />
);

const Chip = ({ tone = "default", dot, icon, children }: ChipProps) => {
  const palette = TONE_STYLES[tone];
  const dotTone: ChipTone | null =
    dot === true ? "default" : dot ? (dot as ChipTone) : null;

  return (
    <Box
      as="span"
      {...BASE}
      bg={palette.bg}
      borderColor={palette.borderColor}
      color={palette.color}
    >
      {dotTone && <ChipDot tone={dotTone} />}
      {icon && (
        <Box as="span" display="inline-flex" alignItems="center" lineHeight="1">
          {icon}
        </Box>
      )}
      <Box as="span" lineHeight="1">
        {children}
      </Box>
    </Box>
  );
};

export default Chip;
